import { useLastBreadcrumbTitle, useUuidEntityTitle } from "@/common/helpers/breadcrumb";
import { isUuid } from "@/common/helpers/utils";
import type { NavigationItem } from "@/config/navigation/types";
import { useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Sets document.title based on the current location and active navigation item.
 *
 * Title composition priority:
 * 1. UUID detail page (/section/uuid)           → "Entity Name | Section"
 * 2. Action on entity (/section/uuid/action)    → "Action | Entity Name"
 * 3. Static action page (/section/action)       → "Action | Section"
 * 4. Section / fallback                         → "Section" or "Page Not Found"
 *
 * While UUID data is loading, falls back to the next applicable level.
 */
export function useDocumentTitle(
  location: string,
  activeNavigationItem: NavigationItem | undefined,
  moduleNavigationItem: NavigationItem | undefined,
  isReady: boolean,
): void {
  const { t } = useTranslation("common");
  const lastBreadcrumbTitle = useLastBreadcrumbTitle(location);

  const pathParts = location.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1] ?? "";
  const secondToLastPart = pathParts[pathParts.length - 2] ?? "";
  const isLastPartUuid = isUuid(lastPart);
  const isSecondToLastPartUuid = !isLastPartUuid && isUuid(secondToLastPart);

  const uuidEntityTitle = useUuidEntityTitle(isLastPartUuid ? secondToLastPart : "", isLastPartUuid ? lastPart : "");

  const parentUuidEntityTitle = useUuidEntityTitle(
    isSecondToLastPartUuid ? (pathParts[pathParts.length - 3] ?? "") : "",
    isSecondToLastPartUuid ? secondToLastPart : "",
  );

  useLayoutEffect(() => {
    if (!isReady) return;

    const activeNavTitle = activeNavigationItem?.title();
    let title: string;

    if (uuidEntityTitle && activeNavTitle) {
      title = `${uuidEntityTitle} | ${activeNavTitle}`;
    } else if (parentUuidEntityTitle && lastBreadcrumbTitle) {
      title = `${lastBreadcrumbTitle} | ${parentUuidEntityTitle}`;
    } else if (lastBreadcrumbTitle && activeNavTitle && lastBreadcrumbTitle !== activeNavTitle) {
      title = `${lastBreadcrumbTitle} | ${activeNavTitle}`;
    } else {
      title = lastBreadcrumbTitle ?? activeNavTitle ?? t("PageNotFound");
    }

    const moduleTitleStr = moduleNavigationItem?.title();
    if (activeNavigationItem && moduleTitleStr && activeNavigationItem.id !== moduleNavigationItem?.id) {
      title = `${title} | ${moduleTitleStr}`;
    }

    document.title = t("TitleWithApp", { title });
  }, [
    isReady,
    activeNavigationItem,
    moduleNavigationItem,
    lastBreadcrumbTitle,
    uuidEntityTitle,
    parentUuidEntityTitle,
    t,
  ]);
}
